import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { USER_TYPES } from '../../constants/userTypes'
import { authAPI } from '../../services/api'

const AppleCallback = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const [isProcessing, setIsProcessing] = useState(true)
    const [error, setError] = useState(null)
    const { t, i18n } = useTranslation()

    useEffect(() => {
        const handleAppleCallback = async () => {
            try {
                // Get URL parameters
                const urlParams = new URLSearchParams(location.search)
                const code = urlParams.get('code')
                const error = urlParams.get('error')
                const state = urlParams.get('state')

                console.log('Apple Callback Params:', {
                    hasCode: !!code,
                    hasError: !!error,
                    state: state,
                    allParams: Object.fromEntries(urlParams.entries())
                })

                if (error) {
                    setError(t('appleCallback.errors.generic'))
                    setIsProcessing(false)
                    return
                }

                if (!code) {
                    // Check if this is a login response from Apple
                    const userParam = urlParams.get('user')
                    if (!userParam && !code) {
                        setError(t('appleCallback.errors.missingCode'))
                        setIsProcessing(false)
                        return
                    }
                }

                // Get user type from store
                const userType = localStorage.getItem('apple_login_user_type') || 'pet_guardian'

                // Parse user data if available (Apple sends first/last name here only on first login)
                let appleUser = null;
                const userParam = urlParams.get('user');
                if (userParam) {
                    try {
                        appleUser = JSON.parse(userParam);
                    } catch (e) {
                        console.warn('Failed to parse Apple user data:', e);
                    }
                }

                // Exchange code/data for tokens and user data
                const response = await authAPI.appleCallback({
                    code,
                    state,
                    user: userParam,
                    user_type: userType,
                    first_name: appleUser?.name?.firstName,
                    last_name: appleUser?.name?.lastName
                })

                const data = response.data.data || response.data

                if (response.status === 200 || response.status === 201) {
                    // Unified handling for both new and existing users
                    // The backend now creates user immediately if new

                    localStorage.setItem('access_token', data.access_token)
                    localStorage.setItem('refresh_token', data.refresh_token)
                    localStorage.setItem('user_data', JSON.stringify(data.user))

                    // User logged in successfully
                    setTimeout(() => {
                        window.dispatchEvent(new CustomEvent('user-logged-in'));
                    }, 1000);

                    localStorage.removeItem('apple_login_user_type')

                    const actualUserType = data.user.user_type || data.user.userType || data.user.type
                    const userTypeSlug = actualUserType.toLowerCase()
                    let redirectRoute = '/social'

                    // If user is new OR profile is incomplete, redirect directly to complete-profile
                    if (data.is_new_user || data.user.profile_status === 'incomplete') {
                        redirectRoute = `/auth/complete-profile/${userTypeSlug}`
                    } else if (actualUserType === USER_TYPES.ADMIN) {
                        redirectRoute = '/admin/dashboard'
                    } else if (actualUserType === USER_TYPES.CLINIC_MODERATOR) {
                        redirectRoute = '/medical/clinic/appointment-management/appointments'
                    } else {
                        redirectRoute = localStorage.getItem('apple_login_intended_route') || '/social'
                    }

                    localStorage.removeItem('apple_login_intended_route')

                    navigate(redirectRoute, {
                        state: {
                            loginSuccess: true,
                            user: data.user,
                            loginMethod: 'apple',
                            isNewUser: !!data.is_new_user
                        }
                    })
                } else {
                    setError(data.message || t('appleCallback.errors.generic'))
                    setIsProcessing(false)
                }
            } catch (error) {
                console.error('Apple Callback error:', error)
                setError(error.response?.data?.message || t('appleCallback.errors.generic'))
                setIsProcessing(false)
            }
        }

        handleAppleCallback()
    }, [location, navigate, t])

    if (error) {
        return (
            <div style={{
                width: '100vw',
                height: '100vh',
                background: '#FAFAFA',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: '"Red Hat Display", sans-serif'
            }}>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '16px',
                    textAlign: 'center',
                    padding: '20px'
                }}>
                    <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '50%',
                        background: '#EF4444',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M18 6L6 18M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                    <h2 style={{ fontWeight: 600, fontSize: '24px', color: '#1F2937', margin: 0 }}>
                        {t('appleCallback.errorView.title', 'Authentication Failed')}
                    </h2>
                    <p style={{ fontSize: '16px', color: '#6B7280', margin: 0, maxWidth: '400px' }}>
                        {error}
                    </p>
                    <button
                        onClick={() => navigate('/auth/login')}
                        style={{
                            padding: '12px 24px',
                            background: '#000',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontWeight: 500,
                            fontSize: '16px',
                            cursor: 'pointer',
                            marginTop: '16px'
                        }}
                    >
                        {t('appleCallback.errorView.retry', 'Try Again')}
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div style={{
            width: '100vw',
            height: '100vh',
            background: '#FAFAFA',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: '"Red Hat Display", sans-serif'
        }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                <Loader2 size={48} className="animate-spin" style={{ color: '#000' }} />
                <p style={{ fontSize: '18px', color: '#6B7280', margin: 0 }}>
                    {t('appleCallback.loadingMessage', 'Authenticating with Apple...')}
                </p>
            </div>
        </div>
    )
}

export default AppleCallback
